import { NetworkStatus } from '@apollo/client';
import { AppConnectionStatuses } from 'integrations-lib/constants/appConnectionStatuses';

import { TReconnectMetadata } from '../types/reconnect-metadata';
import { useDataSyncApps } from './useDataSyncApps';
import { getReconnectionConfig } from '../utils/getReconnectionConfig';
import { useDashworksAppIds } from './useDashworksAppIds';
import { useMcpSupportedApps } from './useMcpSupportedApps';
import { useUnifiedAccountSupportedApps } from './useUnifiedAccountSupportedApps';
import { TReconnectConfig } from '../types/reconnection-config';

const OAUTHOR_MIGRATION_ORIGIN = 'OAUTHOR';

export const useServiceComponent = ({
  metadata,
  url,
  reconnectionConfig: reconnectionConfigProp,
  skip,
  displayForAllStatuses,
}: {
  metadata?: TReconnectMetadata;
  url?: string;
  reconnectionConfig?: TReconnectConfig;
  skip?: boolean;
  displayForAllStatuses?: boolean;
}) => {
  const appId = metadata?.appId || 0;
  const status = metadata?.status;

  const {
    data: unifiedAccountApps,
    loading: unifiedAccountAppsLoading,
    error: unifiedAccountAppsError,
    networkStatus: unifiedAccountAppsNetworkStatus,
  } = useUnifiedAccountSupportedApps({
    skip: skip || !!reconnectionConfigProp,
  });

  const {
    data: dataSyncApps,
    isLoading: dataSyncAppsLoading,
    error: dataSyncAppsError,
    networkStatus: dataSyncAppsNetworkStatus,
  } = useDataSyncApps({ skip: skip || !!reconnectionConfigProp });

  const {
    data: dashworksAppIds,
    isLoading: dashworksAppIdsLoading,
    error: dashworksAppIdsError,
    networkStatus: dashworksAppsNetworkStatus,
  } = useDashworksAppIds({ skip: skip || !!reconnectionConfigProp });

  const {
    data: mcpServers,
    loading: mcpServersLoading,
    error: mcpServersError,
    networkStatus: mcpServersNetworkStatus,
  } = useMcpSupportedApps({ skip: skip || !!reconnectionConfigProp });

  const isDataLoaded =
    dataSyncAppsNetworkStatus >= NetworkStatus.ready &&
    dashworksAppsNetworkStatus >= NetworkStatus.ready &&
    unifiedAccountAppsNetworkStatus >= NetworkStatus.ready &&
    mcpServersNetworkStatus >= NetworkStatus.ready;

  const error =
    dataSyncAppsError ||
    dashworksAppIdsError ||
    unifiedAccountAppsError ||
    mcpServersError;

  const unifiedAccountApp = unifiedAccountApps.find(
    ({ appId: unifiedAccountAppId }) => unifiedAccountAppId === appId
  );
  const isUnifiedAccountSupportedApp = !!unifiedAccountApp;
  const isUnifiedAccountMigration =
    unifiedAccountApp?.migrationOrigin === OAUTHOR_MIGRATION_ORIGIN;

  const isDataSyncApp = dataSyncApps.some(
    ({ appId: dataSyncAppId }) => dataSyncAppId === appId
  );

  const isDashworksApp = dashworksAppIds.some(
    (dashworkAppId) => dashworkAppId === appId
  );

  const isMcpSupportedApp = mcpServers.some(
    ({ mcpAppId }) => mcpAppId === appId
  );

  let reconnectionConfig = reconnectionConfigProp ?? null;

  if (!reconnectionConfig && isDataLoaded && !error) {
    reconnectionConfig = getReconnectionConfig({
      appId,
      isDataSyncApp,
      isDashworksApp,
      isMcpSupportedApp,
      isUnifiedAccountSupportedApp,
      hasUrl: !!url,
    });
  }

  const { statusesForDisplaying, serviceComponent: ServiceComponent = null } =
    reconnectionConfig || ({} as TReconnectConfig);

  const isVisible =
    displayForAllStatuses ||
    !statusesForDisplaying?.length ||
    statusesForDisplaying.includes(status as AppConnectionStatuses);

  return {
    serviceComponent: isVisible ? ServiceComponent : null,
    loading:
      dataSyncAppsLoading ||
      dashworksAppIdsLoading ||
      unifiedAccountAppsLoading ||
      mcpServersLoading,
    error,
    isLoaded: isDataLoaded,
    isUnifiedAccountMigration,
  };
};
